import React, { useEffect, useRef, useState } from 'react';

export type Tab = {
  id: string;
  name: string;
  disabled?: boolean;
  onClick?: () => void;
  to?: string;
  tooltip?: string;
  icon?: string;
  bubble?: boolean;
};

type UseHeaderTabsParams = {
  tabs: Tab[];
  tabActive?: string;
  onChangeTab?: (id: string) => void;
  onDisableTab: (id: string) => void;
  isNavigation: boolean;
};

// Una pestaña activa deshabilitada no puede recibir el foco: el Tab entra por la primera habilitada.
function getRovingTabIndex(tabs: Tab[], activeId?: string): number {
  const activeIndex = tabs.findIndex((tab) => tab.id === activeId && !tab.disabled);
  return activeIndex === -1 ? tabs.findIndex((tab) => !tab.disabled) : activeIndex;
}

function useHeaderTabs({ tabs, tabActive, onChangeTab, onDisableTab, isNavigation }: UseHeaderTabsParams) {
  const [internalActiveTab, setInternalActiveTab] = useState(tabActive);
  const previousTabActiveRef = useRef(tabActive);
  const tabRefs = useRef<Array<HTMLElement | null>>([]);

  // In navigation mode the route owns the highlight: an option that does not change the URL
  // (an external link, for instance) must not stay marked as current.
  const activeTabState = isNavigation ? tabActive : internalActiveTab;

  function activateTab(tab: Tab): void {
    setInternalActiveTab(tab.id);
    onChangeTab?.(tab.id);
  }

  function onClickChangeTab(tab: Tab): void {
    if (!isNavigation) {
      if (tab.disabled) onDisableTab(tab.id);
      else activateTab(tab);
    }
    tab.onClick?.();
  }

  useEffect(() => {
    if (tabActive !== previousTabActiveRef.current && tabActive !== internalActiveTab) {
      setInternalActiveTab(tabActive);
    }
    previousTabActiveRef.current = tabActive;
  }, [tabActive, internalActiveTab]);

  function registerTabRef(index: number): (el: HTMLElement | null) => void {
    return (el: HTMLElement | null) => {
      tabRefs.current[index] = el;
    };
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLElement>, currentIndex: number): void {
    // Arrow keys belong to the tabs pattern. Inside a navigation landmark each arrow would
    // trigger a route change, so only Tab walks the options.
    if (isNavigation) return;

    const enabledIndexes = tabs
      .map((tab, i) => ({ tab, i }))
      .filter(({ tab }) => !tab.disabled)
      .map(({ i }) => i);

    if (enabledIndexes.length === 0) return;

    const posInEnabled = enabledIndexes.indexOf(currentIndex);
    let nextIndex: number | null = null;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        nextIndex = enabledIndexes[(posInEnabled + 1) % enabledIndexes.length];
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        nextIndex = enabledIndexes[(posInEnabled - 1 + enabledIndexes.length) % enabledIndexes.length];
        break;
      case 'Home':
        event.preventDefault();
        nextIndex = enabledIndexes[0];
        break;
      case 'End':
        event.preventDefault();
        nextIndex = enabledIndexes.at(-1) ?? null;
        break;
      default:
        return;
    }

    if (nextIndex !== null) {
      tabRefs.current[nextIndex]?.focus();
      activateTab(tabs[nextIndex]);
    }
  }

  const rovingTabIndex = getRovingTabIndex(tabs, activeTabState);

  return { activeTabState, rovingTabIndex, registerTabRef, onClickChangeTab, handleKeyDown };
}

export default useHeaderTabs;
